import type { ReactNode } from 'react';
import { cn } from '@eureca/ui';
import {
  ConsultantsMenu,
  type ConsultantsMenuItemId,
} from '@/shared/consultants-menu/ConsultantsMenu';

interface ConsultantLayoutProps {
  activeMenuId: ConsultantsMenuItemId;
  onLogout?: () => void;
  onHelp?: () => void;
  children: ReactNode;
  className?: string;
}

// Locks the whole consultant surface to the viewport (`h-dvh`) so the inner
// regions (`Body`, `TableSection`) can opt into internal scroll via flex-1 +
// min-h-0. Page-level scroll is intentionally disabled so the header stays
// pinned and the table/pagination footer hug the viewport edges.
export function ConsultantLayout({
  activeMenuId,
  onLogout,
  onHelp,
  children,
  className,
}: ConsultantLayoutProps) {
  return (
    <div className={cn('flex h-dvh flex-row overflow-hidden bg-card', className)}>
      <ConsultantsMenu activeId={activeMenuId} onLogout={onLogout} onHelp={onHelp} />
      <main className="flex min-w-0 min-h-0 flex-1 flex-col">{children}</main>
    </div>
  );
}

interface SlotProps {
  children: ReactNode;
  className?: string;
}

// Non-scrolling region at the top of `<main>`. Hosts the page title + primary
// actions; consumers can stack a second `Header` for filters when they want
// extra spacing between the two rows.
function Header({ children, className }: SlotProps) {
  return (
    <header className={cn('flex flex-none flex-col gap-4 px-4 pt-6 md:px-8 md:pt-8', className)}>
      {children}
    </header>
  );
}

// Flex-grow region that fills the remaining viewport height. `min-h-0` is
// load-bearing — without it, flex children that need to scroll (the table
// section) won't shrink and overflow the page.
function Body({ children, className }: SlotProps) {
  return (
    <div className={cn('flex min-h-0 flex-1 flex-col px-4 pt-6 pb-6 md:px-8 md:pb-8', className)}>
      {children}
    </div>
  );
}

// Sticky footer for things like bulk action bars. Sits below `Body` and never
// scrolls — `Body` compresses to make room.
function Footer({ children, className }: SlotProps) {
  return (
    <footer
      className={cn(
        'flex flex-none flex-col gap-3 border-t border-border bg-card px-4 py-4 md:px-8',
        className
      )}
    >
      {children}
    </footer>
  );
}

ConsultantLayout.Header = Header;
ConsultantLayout.Body = Body;
ConsultantLayout.Footer = Footer;
